import ReactNativeBiometrics, { BiometryTypes } from 'react-native-biometrics';
import * as Keychain from 'react-native-keychain';
import { biometricLogger } from '../utils/logger';

const rnBiometrics = new ReactNativeBiometrics();

export interface BiometricCapabilities {
  available: boolean;
  biometryType: BiometryTypes | null;
  error?: string;
}

export interface BiometricAuthResult {
  success: boolean;
  error?: string;
}

class BiometricService {
  /**
   * Check if biometric authentication is available on the device
   */
  async isBiometricAvailable(): Promise<BiometricCapabilities> {
    try {
      const { available, biometryType } = await rnBiometrics.isSensorAvailable();
      return {
        available,
        biometryType,
      };
    } catch (error) {
      return {
        available: false,
        biometryType: null,
        error: error instanceof Error ? error.message : 'Unknown error checking biometric availability',
      };
    }
  }

  /**
   * Get the type of biometric authentication available
   * Returns 'FaceID', 'TouchID', 'Biometrics', or null
   */
  async getBiometricType(): Promise<string | null> {
    const { available, biometryType } = await this.isBiometricAvailable();

    if (!available || !biometryType) {
      return null;
    }

    switch (biometryType) {
      case BiometryTypes.FaceID:
        return 'FaceID';
      case BiometryTypes.TouchID:
        return 'TouchID';
      case BiometryTypes.Biometrics:
        return 'Biometrics';
      default:
        return null;
    }
  }

  /**
   * Create biometric keys for authentication
   * This should be called when the user enables biometric login
   */
  async createBiometricKeys(): Promise<BiometricAuthResult> {
    try {
      const { publicKey } = await rnBiometrics.createKeys();
      biometricLogger.debug('Biometric keys created successfully:', publicKey);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create biometric keys',
      };
    }
  }

  /**
   * Delete biometric keys
   * This should be called when the user disables biometric login
   */
  async deleteBiometricKeys(): Promise<BiometricAuthResult> {
    try {
      const { keysDeleted } = await rnBiometrics.deleteKeys();
      return { success: keysDeleted };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete biometric keys',
      };
    }
  }

  /**
   * Check if biometric keys exist
   */
  async biometricKeysExist(): Promise<boolean> {
    try {
      const { keysExist } = await rnBiometrics.biometricKeysExist();
      return keysExist;
    } catch (error) {
      biometricLogger.error('Error checking biometric keys:', error);
      return false;
    }
  }

  /**
   * Prompt user for biometric authentication
   */
  async authenticateWithBiometrics(promptMessage: string = 'Authenticate to continue'): Promise<BiometricAuthResult> {
    try {
      const { success } = await rnBiometrics.simplePrompt({
        promptMessage,
      });

      return { success };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Biometric authentication failed',
      };
    }
  }

  /**
   * Store user credentials securely with biometric protection
   */
  async storeCredentials(username: string, password: string): Promise<BiometricAuthResult> {
    try {
      const biometricType = await this.getBiometricType();

      // BIOMETRY_CURRENT_SET invalidates the stored credential if the set of
      // enrolled biometrics changes (a new fingerprint/face enrolled, or an
      // existing one removed). Apple's recommended posture for sensitive
      // credentials — prevents an attacker with brief access to an unlocked
      // device from enrolling their own biometric to later retrieve stored
      // credentials. Users who legitimately re-enroll will need to sign in
      // with their password once to re-seed the keychain entry.
      await Keychain.setGenericPassword(username, password, {
        accessControl: Keychain.ACCESS_CONTROL.BIOMETRY_CURRENT_SET,
        accessible: Keychain.ACCESSIBLE.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
        authenticationType: Keychain.AUTHENTICATION_TYPE.BIOMETRICS,
        service: 'com.myfinalkey.auth',
      });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to store credentials',
      };
    }
  }

  /**
   * Retrieve stored credentials with biometric authentication
   */
  async getStoredCredentials(promptMessage: string = 'Authenticate to login'): Promise<{
    success: boolean;
    username?: string;
    password?: string;
    error?: string;
  }> {
    try {
      const credentials = await Keychain.getGenericPassword({
        authenticationPrompt: {
          title: 'Authentication Required',
          subtitle: promptMessage,
        },
        service: 'com.myfinalkey.auth',
      });

      if (credentials) {
        return {
          success: true,
          username: credentials.username,
          password: credentials.password,
        };
      } else {
        return {
          success: false,
          error: 'No credentials stored',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to retrieve credentials',
      };
    }
  }

  /**
   * Clear stored credentials
   */
  async clearStoredCredentials(): Promise<BiometricAuthResult> {
    try {
      await Keychain.resetGenericPassword({
        service: 'com.myfinalkey.auth',
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to clear credentials',
      };
    }
  }

  /**
   * Check if biometric login is enabled for the current user
   * Uses AsyncStorage flag to avoid triggering Face ID prompt
   */
  async isBiometricLoginEnabled(): Promise<boolean> {
    try {
      // Check AsyncStorage flag instead of trying to read credentials
      // This avoids triggering a Face ID prompt just to check status
      const AsyncStorage = require('@react-native-async-storage/async-storage').default;
      const biometricEnabled = await AsyncStorage.getItem('biometricEnabled');
      return biometricEnabled === 'true';
    } catch (error) {
      biometricLogger.error('Error checking biometric login status:', error);
      return false;
    }
  }

  /**
   * Enable biometric login for the user
   */
  async enableBiometricLogin(username: string, password: string): Promise<BiometricAuthResult> {
    try {
      // Check if biometrics are available
      const capabilities = await this.isBiometricAvailable();
      if (!capabilities.available) {
        return {
          success: false,
          error: 'Biometric authentication is not available on this device',
        };
      }

      // Create biometric keys
      const keysResult = await this.createBiometricKeys();
      if (!keysResult.success) {
        return keysResult;
      }

      // Store credentials securely
      const storeResult = await this.storeCredentials(username, password);
      if (!storeResult.success) {
        // Clean up keys if credential storage fails
        await this.deleteBiometricKeys();
        return storeResult;
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to enable biometric login',
      };
    }
  }

  /**
   * Disable biometric login for the user
   */
  async disableBiometricLogin(): Promise<BiometricAuthResult> {
    try {
      await this.deleteBiometricKeys();
      await this.clearStoredCredentials();
      // Clear the AsyncStorage flag
      const AsyncStorage = require('@react-native-async-storage/async-storage').default;
      await AsyncStorage.removeItem('biometricEnabled');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to disable biometric login',
      };
    }
  }

  /**
   * Perform biometric login
   */
  async performBiometricLogin(): Promise<{
    success: boolean;
    username?: string;
    password?: string;
    error?: string;
  }> {
    try {
      // Check if biometric login is enabled
      const isEnabled = await this.isBiometricLoginEnabled();
      if (!isEnabled) {
        return {
          success: false,
          error: 'Biometric login is not enabled',
        };
      }

      // Authenticate and retrieve credentials
      const result = await this.getStoredCredentials('Login with biometrics');
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Biometric login failed',
      };
    }
  }
}

export default new BiometricService();
